import React from "react";
import {  List, ChevronRight } from "lucide-react";

export interface PreviewImage {
  id?: number;
  url?: string;
  file?: File;
}

export interface PreviewSection {
  id: number;
  title: string;
  content: string;
  images: PreviewImage[];
}

interface BlogPreviewProps {
  onClose: () => void;
  title: string;
  description: string;
  mainImagePreview: string;
  sections: PreviewSection[];
  doctorName: string;
  category: string;
  date: string;
  readTime: string;
}

const BlogPreview: React.FC<BlogPreviewProps> = ({
  // isOpen prop removed as conditional rendering handles it
  onClose,
  title,
  description,
  mainImagePreview,
  sections,
  doctorName,
  category,
  date,
  readTime,
}) => {
  // if (!isOpen) return null; // Logic moved to parent

  const scrollToSection = (id: number) => {
    const element = document.getElementById(`preview-section-${id}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const renderImageSrc = (img: PreviewImage) => {
    if (img.file) return URL.createObjectURL(img.file);
    if (img.url) return img.url;
    return "";
  };

  return (
    // Removed fixed overlay wrapper
    <div className="bg-white min-h-screen w-full flex flex-col animate-in fade-in duration-200">
      {/* Header */}
      <div className="flex justify-between items-center p-4 border-b bg-gray-50">
        <button
          onClick={onClose}
          className="bg-gray-200 hover:bg-gray-300 rounded-lg px-4 py-2 transition-colors text-gray-700 text-sm font-medium"
        >
          Back
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 md:p-8 bg-white relative scroll-smooth">
        {/* Part 1: Split Header (Title/Desc vs Image) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12 items-start">
          {/* Left: Text */}
          <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 font-medium">
              <span className="bg-blue-100 text-blue-800 text-xs px-3 py-1 rounded-full font-bold uppercase tracking-wide">
                {category || "Uncategorized"}
              </span>
              <span>•</span>
              <span className="text-gray-900 font-semibold">
                {doctorName || "Unknown Doctor"}
              </span>
              <span>•</span>
              <span>{date}</span>
              <span>•</span>
              <span>{readTime}</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 leading-tight text-left break-words">
              {title || "Untitled Blog Title"}
            </h1>
            {description && (
              <div
                className="prose prose-lg max-w-none text-gray-700 leading-relaxed text-left blog-editor-preview break-words"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            )}
          </div>

          {/* Right: Main Image */}
          <div className="flex justify-center lg:justify-end">
            {mainImagePreview ? (
              <div className="rounded-2xl shadow-xl overflow-hidden border border-gray-100 w-full max-w-lg">
                <img
                  src={mainImagePreview}
                  alt="Blog Main"
                  className="w-full h-auto object-cover"
                />
              </div>
            ) : (
              <div className="w-full h-64 bg-gray-100 rounded-2xl flex items-center justify-center text-gray-400 border-2 border-dashed">
                No Main Image
              </div>
            )}
          </div>
        </div>

        <div className="w-full h-px bg-gray-200 my-8"></div>

        {/* Part 2: Split Content (Sections vs TOC) */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 mb-12 relative items-start">
          {/* Main Content (Left ~75%) */}
          <div className="lg:col-span-3 space-y-16">
            {sections.map((s) => (
              <div
                key={s.id}
                id={`preview-section-${s.id}`}
                className="space-y-6 scroll-mt-24"
              >
                <h2 className="text-3xl font-bold text-gray-800 relative pl-6 break-words text-left">
                  <span className="absolute left-0 top-1.5 w-1.5 h-8 bg-blue-600 rounded-full"></span>
                  {s.title}
                </h2>
                <div
                  className="prose prose-lg max-w-none text-gray-700 leading-relaxed blog-editor-preview break-words text-left"
                  dangerouslySetInnerHTML={{ __html: s.content }}
                />
                {s.images.length > 0 && (
                  <div className="flex flex-col items-center justify-center gap-6 mt-6">
                    {s.images.map((img, idx) => (
                      <div
                        key={idx}
                        className="rounded-xl overflow-hidden shadow-lg border border-gray-100 max-w-2xl"
                      >
                        <img
                          src={renderImageSrc(img)}
                          className="w-full h-auto object-cover"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {sections.length === 0 && (
              <p className="text-gray-400 italic">No sections added yet.</p>
            )}
          </div>

          {/* Sidebar TOC (Right ~25% - Sticky) */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="sticky top-4 bg-gray-50 rounded-xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <List size={20} className="text-blue-600" />
                Table of Contents
              </h3>
              {sections.length > 0 ? (
                <nav className="space-y-2">
                  {sections.map((s, idx) => (
                    <button
                      key={s.id}
                      onClick={() => scrollToSection(s.id)}
                      className="block text-left text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 px-3 py-2 rounded-lg transition-all w-full leading-snug group flex items-start"
                    >
                      <ChevronRight
                        size={14}
                        className="mt-0.5 mr-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                      />
                      <span>
                        {idx + 1}. {s.title}
                      </span>
                    </button>
                  ))}
                </nav>
              ) : (
                <p className="text-sm text-gray-400">
                  Add sections to see content here.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Global Style for Content Editable List Defaults */}
      <style>{`
        .blog-editor-preview ul {
            list-style-type: disc;
            padding-left: 1.5rem;
            margin-top: 0.5rem;
            margin-bottom: 0.5rem;
        }
        .blog-editor-preview ol {
            list-style-type: decimal;
            padding-left: 1.5rem;
            margin-top: 0.5rem;
            margin-bottom: 0.5rem;
        }
        .blog-editor-preview li {
            margin-bottom: 0.25rem;
        }
        /* Fix for overlapping text and generic spacing */
        .blog-editor-preview p, .blog-editor-preview div {
            margin-bottom: 1em !important;
            line-height: 1.75 !important;
            height: auto !important;
        }
      `}</style>
    </div>
  );
};

export default BlogPreview;
